import React, { useEffect, useState } from 'react'
import BasicModal from './BasicModal'
import { useTranslation } from 'react-i18next'
import ActionButtons from '@components/Buttons/ActionButtons'
import { Box, MenuItem, TextField, Typography } from '@mui/material'
import { ContainedButtonVariant } from '@components/Buttons/ContainedButton'
import { PotentialInviteDeclineReasonItem } from '@swagger'
import { communities } from '@api/swagger'
import { useAppSnackbar } from '@hooks/useAppSnackbar'
import useLoadingContext from '@hooks/useLoadingContext'
import { TextButtonVariant } from '@components/Buttons/TextButton'
import { AwaitingDecisionRow } from '@components/Communities/getAwaitingDecisionsTableColumns'
import { useLoadingIds } from '@hooks/useLoadingIds'

interface PassPotentialInviteModalProps {
  isOpen: boolean
  potentialInvite: AwaitingDecisionRow
  onClose: () => void
  onComplete: () => void
  communityKey: number
  /** LCD a CSL passes for. When undefined, the acting actor passes. */
  actorKey?: number
}

const PassPotentialInviteModal: React.FC<PassPotentialInviteModalProps> = ({
  isOpen,
  potentialInvite: {
    familyName,
    potentialInvite: { potentialInviteKey },
  },
  communityKey,
  actorKey,
  onClose,
  onComplete,
}) => {
  const { t } = useTranslation()
  const { handleError } = useAppSnackbar()
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [reasons, setReasons] = useState<PotentialInviteDeclineReasonItem[]>([])
  const {
    AwaitingDecisionsTable: {
      declinePotentialInvite: declinePotentialInviteLoadingId,
      fetchPotentialInviteDeclineReasons:
        fetchPotentialInviteDeclineReasonsLoadingId,
    },
  } = useLoadingIds()

  const fetchErrorMessage = t(
    'PotentialInvite.Error.FetchPotentialInviteDeclineReasons',
    'An error occurred while retrieving decline reasons.'
  )

  const declineErrorMessage = t(
    'PotentialInvite.Error.DeclinePotentialInvite',
    'Failed to decline the potential invite.'
  )

  const resetModalState = () => {
    onClose()
    setSelectedReason('')
  }

  const fetchReasons = async (): Promise<void> => {
    try {
      const { declineReasons } =
        await communities.fetchPotentialInviteDeclineReasons()
      setReasons(declineReasons)
    } catch (error) {
      await handleError(error, fetchErrorMessage)
    }
  }

  const {
    triggerFetch: triggerFetchReasons,
    isLoading: isLoadingFetchReasons,
  } = useLoadingContext({
    loadingId: fetchPotentialInviteDeclineReasonsLoadingId,
    asyncFunction: fetchReasons,
  })

  useEffect(() => {
    triggerFetchReasons()
  }, [potentialInviteKey, triggerFetchReasons])

  const handleFormSubmit = (event: React.FormEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  const declinePotentialInvite = async (): Promise<void> => {
    try {
      await communities.declinePotentialInvite({
        potentialInviteKey,
        communityKey,
        body: { potentialInviteDeclineReasonKey: +selectedReason, actorKey },
      })
      resetModalState()
      onComplete()
    } catch (error) {
      await handleError(error, declineErrorMessage)
    }
  }

  const {
    triggerFetch: triggerDeclinePotentialInvite,
    isLoading: isLoadingDeclinePotentialInvite,
  } = useLoadingContext({
    loadingId: declinePotentialInviteLoadingId,
    asyncFunction: declinePotentialInvite,
  })

  const handleConfirm = () => {
    triggerDeclinePotentialInvite()
  }

  return (
    <BasicModal
      disableSpacingBody
      isOpen={isOpen}
      maxWidth="xs"
      handleFormSubmit={handleFormSubmit}
      dialogActions={
        <ActionButtons
          primaryButtonLabel={ContainedButtonVariant.Save}
          primaryClick={handleConfirm}
          secondaryButtonLabel={TextButtonVariant.Cancel}
          secondaryClick={resetModalState}
          disablePrimaryButton={
            isLoadingFetchReasons ||
            !selectedReason ||
            isLoadingDeclinePotentialInvite
          }
        />
      }
      dialogTitle={t('AwaitingDecisions.PassModal.Title', 'Awaiting Decisions')}
      dialogTitleStyle={{
        textAlign: 'center',
      }}
      dialogContent={
        <Box>
          <Typography textAlign={'left'} component="p" whiteSpace={'pre-line'}>
            {t(
              'PotentialInvite.PassModal.Information',
              `Please select a reason for passing on {{familyName}} family.
               This will be recorded for your records.`,
              { familyName }
            )}
          </Typography>

          <Box pt={2}>
            <TextField
              name="passPotentialInviteReason"
              id="pass-potential-invite-reason"
              variant="filled"
              value={selectedReason}
              label={t('PotentialInvite.PassModal.Select.Label', 'Reason')}
              onChange={(event) => {
                setSelectedReason(event.target.value)
              }}
              fullWidth
              select
            >
              {reasons?.map(
                ({ potentialInviteDeclineReasonKey, description }) => (
                  <MenuItem
                    key={potentialInviteDeclineReasonKey}
                    value={potentialInviteDeclineReasonKey}
                  >
                    {t(
                      'PotentialInvite.PassModal.Select.Option',
                      '{{description}}',
                      { description }
                    )}
                  </MenuItem>
                )
              )}
            </TextField>
          </Box>
        </Box>
      }
    />
  )
}
export default PassPotentialInviteModal
